import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader } from "./ui/card";
import {
  Calendar,
  Clock,
  CheckCircle,
  Phone,
  Video,
  MapPin,
} from "lucide-react";

const DemoSection = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    cafeName: "",
    location: "",
  });
  const [loading, setLoading] = useState(false);

  const availableSlots = [
    { time: "10:00 AM", available: true },
    { time: "11:30 AM", available: true },
    { time: "2:00 PM", available: false },
    { time: "3:30 PM", available: true },
    { time: "5:00 PM", available: true },
    { time: "6:30 PM", available: true },
  ];

  const demoIncludes = [
    "Live QR menu demonstration",
    "Payment gateway walkthrough",
    "Analytics dashboard tour",
    "Custom setup consultation",
    "Pricing discussion & offers",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.phone ||
      !formData.cafeName ||
      !selectedDate ||
      !selectedTime
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    setLoading(true);

    const templateParams = {
      name: formData.name,
      phone: formData.phone,
      cafeName: formData.cafeName,
      location: formData.location || "Not specified",
      selectedDate,
      selectedTime,
    };

    try {
      const result = await emailjs.send(
        "service_0u87cun", // 🔹 Replace with your EmailJS Service ID
        "template_675slhw", // 🔹 Replace with your EmailJS Template ID
        templateParams,
        "IQVL8sHRO218k1NrG" // 🔹 Replace with your Public Key
      );

      console.log("Email sent:", result.text);
      alert("✅ Demo scheduled successfully! We’ll contact you soon.");
      setFormData({ name: "", phone: "", cafeName: "", location: "" });
      setSelectedDate("");
      setSelectedTime("");
    } catch (error) {
      console.error("EmailJS error:", error);
      alert("❌ Failed to send demo request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 py-20"
      id="demo"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Book Your{" "}
            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Free Demo
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See FineDine in action! Get a personalized demo and discover how we
            can transform your cafe.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Demo Booking Form */}
          <Card className="bg-white shadow-xl border-0">
            <CardHeader className="text-center pb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Schedule Your Demo
              </h3>
              <p className="text-gray-600">
                Choose your preferred time and we'll call you
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Cafe Information */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cafe Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Your cafe name"
                      value={formData.cafeName}
                      onChange={(e) =>
                        setFormData({ ...formData, cafeName: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="City name"
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Date & Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    required
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Preferred Time *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.time}
                        type="button"
                        disabled={!slot.available}
                        className={`p-3 rounded-lg text-sm font-medium border-2 transition-all ${
                          selectedTime === slot.time
                            ? "border-orange-500 bg-orange-50 text-orange-700"
                            : slot.available
                            ? "border-gray-300 hover:border-orange-300 hover:bg-orange-50"
                            : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        onClick={() =>
                          slot.available && setSelectedTime(slot.time)
                        }
                      >
                        <Clock className="h-4 w-4 mx-auto mb-1" />
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-4 text-lg font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      <Calendar className="mr-2 h-5 w-5" />
                      Schedule My Free Demo
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Right Side Info */}
          <div className="space-y-8">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Video className="mr-3 h-6 w-6 text-orange-600" />
                  What's Included in Demo
                </h3>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {demoIncludes.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg border-0">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-6">Prefer to Talk Now?</h3>
                <div className="space-y-4">
                  <a
                    href="tel:+91-9318475412"
                    className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm rounded-lg p-4 hover:bg-white/30 transition-colors"
                  >
                    <Phone className="h-5 w-5" />
                    <div>
                      <div className="font-semibold">Call Us Now</div>
                      <div className="text-sm opacity-90">+91-9318475412</div>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/919761171602"
                    className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm rounded-lg p-4 hover:bg-white/30 transition-colors"
                  >
                    <div className="h-5 w-5 bg-green-500 rounded flex items-center justify-center text-xs font-bold">
                      W
                    </div>
                    <div>
                      <div className="font-semibold">WhatsApp Us</div>
                      <div className="text-sm opacity-90">
                        Instant response guaranteed
                      </div>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>

            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <h4 className="font-semibold text-gray-900 mb-2">
                Demo Available
              </h4>
              <p className="text-gray-600 mb-4">
                Monday to Saturday, 9 AM - 9 PM
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <MapPin className="h-4 w-4" />
                <span>Available across India</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
